/**
 * The first-publish hand-off: the guide's Review step passes the "You're live" message to the listing page through
 * the query cache. Router state cannot carry it: with `v7_startTransition` the guide's own redirect for a published
 * listing renders first and navigates with its (empty) state, so the hand-off must not depend on which navigation
 * lands last.
 */

import type { QueryClient } from '@tanstack/react-query';

/** A hand-off older than this was never picked up (the page did not mount): it must not surface on a later visit. */
const MAX_AGE_MS = 60_000;

interface GoLiveHandoff {
  message: string;
  at: number;
}

const goLiveHandoffKey = (listingId: string) => ['listing-go-live', listingId] as const;

export function putGoLiveHandoff(queryClient: QueryClient, listingId: string, message: string): void {
  queryClient.setQueryData<GoLiveHandoff>(goLiveHandoffKey(listingId), { message, at: Date.now() });
}

/** The fresh hand-off, if any. Pure (safe in a render that React may discard and redo); see clearGoLiveHandoff. */
export function peekGoLiveHandoff(queryClient: QueryClient, listingId: string): string | null {
  const handoff = queryClient.getQueryData<GoLiveHandoff>(goLiveHandoffKey(listingId));
  return handoff && Date.now() - handoff.at <= MAX_AGE_MS ? handoff.message : null;
}

/** Removes the hand-off once the page has taken it, so it is shown once. Call from an effect. */
export function clearGoLiveHandoff(queryClient: QueryClient, listingId: string): void {
  queryClient.removeQueries({ queryKey: goLiveHandoffKey(listingId) });
}
